import React, { useRef, useState } from 'react';
import { FiX, FiCheck, FiAlertCircle } from 'react-icons/fi';
import '../../styles/AdminDashboard.css';

/* ── Types ── */
interface Props {
  userId:     number;
  userEmail:  string;
  userName:   string;
  onClose:    () => void;
  onApproved: () => void;
}

type Phase = 'input' | 'provisioning' | 'connecting' | 'done' | 'error';

type StepStatus = 'pending' | 'running' | 'done' | 'error' | 'skipped';

interface StepView {
  step:       string;
  label:      string;
  status:     StepStatus;
  message?:   string;
  durationMs?: number;
}

const STEP_ORDER = [
  'create-vm',
  'install-hermes',
  'configure-gateway',
  'create-tunnel',
  'configure-composio',
  'configure-telegram',
  'verify',
  'save-config',   // our final connect-admin step
];

const STEP_LABELS: Record<string, string> = {
  'create-vm':          'Create Orgo VM',
  'install-hermes':     'Install Hermes agent',
  'configure-gateway':  'Start chat gateway',
  'create-tunnel':      'Open public tunnel',
  'configure-composio': 'Configure Composio',
  'configure-telegram': 'Configure Telegram',
  'verify':             'Verify agent is reachable',
  'save-config':        'Save configuration for user',
};

const initialSteps = (): StepView[] =>
  STEP_ORDER.map(s => ({ step: s, label: STEP_LABELS[s], status: 'pending' }));

function upsertStep(list: StepView[], next: Partial<StepView> & { step: string }): StepView[] {
  const idx = list.findIndex(s => s.step === next.step);
  if (idx === -1) return list;
  const copy = list.slice();
  copy[idx] = { ...copy[idx], ...next };
  return copy;
}

/** If message is a JSON string, extract a human-readable field; otherwise return as-is. */
function sanitizeMessage(msg: string | undefined): string | undefined {
  if (!msg) return msg;
  const trimmed = msg.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return msg;
  try {
    const parsed = JSON.parse(trimmed);
    if (typeof parsed === 'object' && parsed !== null) {
      const readable =
        parsed.message ?? parsed.error ?? parsed.result ?? parsed.status ?? parsed.detail ?? parsed.text;
      if (typeof readable === 'string' && readable.trim()) return readable.trim();
      // If it's a flat object with no known text field, suppress the JSON
      return undefined;
    }
  } catch {
    // not valid JSON — show as-is
  }
  return msg;
}

function parseSseEvent(raw: string): { event: string; data: Record<string, unknown> } | null {
  let event = 'message';
  const dataLines: string[] = [];
  for (const line of raw.split('\n')) {
    if (line.startsWith('event: '))      event = line.slice(7).trim();
    else if (line.startsWith('data: '))  dataLines.push(line.slice(6));
  }
  if (!dataLines.length) return null;
  try {
    return { event, data: JSON.parse(dataLines.join('\n')) };
  } catch {
    return null;
  }
}

/* ── Step row ── */
function StepRow({ step }: { step: StepView }) {
  const { status, label, message, durationMs } = step;

  const icon = (() => {
    if (status === 'done')    return <span className="apm-step-icon apm-step-icon--done"><FiCheck size={12} /></span>;
    if (status === 'error')   return <span className="apm-step-icon apm-step-icon--error"><FiAlertCircle size={12} /></span>;
    if (status === 'skipped') return <span className="apm-step-icon apm-step-icon--skip">—</span>;
    if (status === 'running') return <span className="apm-step-icon apm-step-icon--running"><span className="apm-pulse-dot" /></span>;
    return <span className="apm-step-icon apm-step-icon--pending" />;
  })();

  return (
    <li className={`apm-step-row apm-step-row--${status}`}>
      {icon}
      <div className="apm-step-body">
        <div className="apm-step-top">
          <span className="apm-step-label">{label}</span>
          {durationMs != null && (
            <span className="apm-step-dur">{(Math.round(durationMs / 100) / 10).toFixed(1)}s</span>
          )}
        </div>
        {message && <div className="apm-step-msg">{message}</div>}
      </div>
    </li>
  );
}

/* ── Modal ── */
const ApproveModal: React.FC<Props> = ({ userId, userEmail, userName, onClose, onApproved }) => {
  const defaultName = (userName.trim() || userEmail.split('@')[0])
    .toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

  const [agentName, setAgentName] = useState(defaultName);
  const [phase, setPhase]         = useState<Phase>('input');
  const [steps, setSteps]         = useState<StepView[]>(initialSteps);
  const [errorMsg, setErrorMsg]   = useState('');
  const listRef                   = useRef<HTMLOListElement>(null);

  const isWorking = phase === 'provisioning' || phase === 'connecting';

  const handleProvision = async () => {
    if (!agentName.trim()) return;

    setPhase('provisioning');
    setSteps(initialSteps());
    setErrorMsg('');

    const token = localStorage.getItem('authToken') || '';

    /* ── 1. Stream /api/provision/start ── */
    let doneData: { computerId: string; gatewayUrl: string; gatewayKey: string } | null = null;
    let finalError: string | null = null;

    try {
      const res = await fetch(`${(process.env.REACT_APP_MOMENTUMCLAW_URL || 'https://momentumclawapi-stage.momentumamp.com')}/api/provision/start`, {
        method: 'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ agentName: agentName.trim(), includeTelegram: false }),
      });

      if (!res.ok || !res.body) {
        throw new Error(`Provision request failed (${res.status})`);
      }

      const reader  = res.body.getReader();
      const decoder = new TextDecoder();
      let   buffer  = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        let idx: number;
        while ((idx = buffer.indexOf('\n\n')) !== -1) {
          const raw = buffer.slice(0, idx);
          buffer    = buffer.slice(idx + 2);

          const evt = parseSseEvent(raw);
          if (!evt) continue;

          if (evt.event === 'step') {
            const p = evt.data as { step: string; status: StepStatus; message?: string; durationMs?: number };
            setSteps(prev => upsertStep(prev, {
              step:       p.step,
              status:     p.status,
              message:    sanitizeMessage(p.message),
              durationMs: p.durationMs,
            }));

          } else if (evt.event === 'done') {
            const p = evt.data as { computerId: string; gatewayUrl: string; gatewayKey: string; hostname: string };
            doneData = { computerId: p.computerId, gatewayUrl: p.gatewayUrl, gatewayKey: p.gatewayKey };
            // Mark verify as done if not already (it's the last provision step)
            setSteps(prev => upsertStep(prev, { step: 'verify', status: 'done' }));

          } else if (evt.event === 'error') {
            finalError = (evt.data as { message?: string }).message ?? 'Provisioning failed';
          }

          // scroll list to bottom
          if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
        }
      }
    } catch (err) {
      finalError = err instanceof Error ? err.message : 'Provisioning failed';
    }

    if (finalError) {
      setErrorMsg(finalError);
      setPhase('error');
      return;
    }

    if (!doneData) {
      setErrorMsg('Provisioning completed but no VM data was returned.');
      setPhase('error');
      return;
    }

    /* ── 2. POST /api/connect-admin ── */
    setPhase('connecting');
    setSteps(prev => upsertStep(prev, { step: 'save-config', status: 'running' }));

    try {
      const connectRes = await fetch(`${(process.env.REACT_APP_MOMENTUMCLAW_URL || 'https://momentumclawapi-stage.momentumamp.com')}/api/connect-admin`, {
        method: 'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          userId:     String(userId),
          computerId: doneData.computerId,
          gatewayUrl: doneData.gatewayUrl,
          gatewayKey: doneData.gatewayKey,
        }),
      });

      if (!connectRes.ok) {
        const body = await connectRes.json().catch(() => ({})) as { error?: string };
        throw new Error(body.error ?? `connect-admin failed (${connectRes.status})`);
      }

      setSteps(prev => upsertStep(prev, {
        step:    'save-config',
        status:  'done',
        message: `Linked to user #${userId} — ${userEmail}`,
      }));
      setPhase('done');
      onApproved();

    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Saving config failed';
      setErrorMsg(msg);
      setSteps(prev => upsertStep(prev, { step: 'save-config', status: 'error', message: msg }));
      setPhase('error');
    }
  };

  return (
    <div
      className="apm-backdrop"
      onClick={e => { if (e.target === e.currentTarget && !isWorking) onClose(); }}
    >
      <div className="apm-modal">

        {/* Header */}
        <div className="apm-modal__header">
          <div className="apm-modal__title-block">
            <div className="apm-modal__title">Provision Momentum Claw Agent</div>
            <div className="apm-modal__subtitle">{userEmail}</div>
          </div>
          {!isWorking && (
            <button className="apm-modal__close" onClick={onClose} title="Close">
              <FiX size={16} />
            </button>
          )}
        </div>

        <div className="apm-modal__body">

          {/* ── Input phase ── */}
          {phase === 'input' && (
            <>
              <label className="apm-label">AGENT NAME</label>
              <input
                className="apm-input"
                type="text"
                value={agentName}
                onChange={e =>
                  setAgentName(
                    e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
                  )
                }
                placeholder="my-agency-agent"
                maxLength={80}
                autoFocus
              />
              <p className="apm-hint">
                A cloud VM + Hermes agent will be provisioned and linked to <strong className="apm-hint__email">{userEmail}</strong>. This takes ~3–5 minutes.
              </p>
              <button
                className="apm-btn-primary"
                onClick={handleProvision}
                disabled={!agentName.trim()}
              >
                Create Agent
              </button>
            </>
          )}

          {/* ── Provisioning / done / error: step list ── */}
          {phase !== 'input' && (
            <>
              <div className="apm-phase-title">
                {phase === 'done'
                  ? 'Agent is ready'
                  : phase === 'error'
                    ? 'Something went wrong'
                    : phase === 'connecting'
                      ? 'Saving configuration…'
                      : 'Provisioning your agent…'}
              </div>
              {(phase === 'provisioning' || phase === 'connecting') && (
                <p className="apm-hint apm-hint--tight">Please keep this window open.</p>
              )}

              <ol className="apm-step-list" ref={listRef}>
                {steps.map(s => <StepRow key={s.step} step={s} />)}
              </ol>

              {phase === 'done' && (
                <div className="apm-result apm-result--success">
                  <FiCheck size={15} />
                  Agent provisioned and approved for <strong>&nbsp;{userEmail}</strong>
                </div>
              )}

              {phase === 'error' && (
                <div className="apm-result apm-result--error">
                  <FiAlertCircle size={15} />
                  {errorMsg}
                </div>
              )}

              {(phase === 'done' || phase === 'error') && (
                <button className="apm-btn-secondary" onClick={onClose}>Close</button>
              )}
            </>
          )}

        </div>
      </div>
    </div>
  );
};

export default ApproveModal;

